import {
  useCreateWordMatMutation,
  useDeleteWordMatMutation,
} from '@/app/redux/endpoints/wordMats';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useWordMats } from '@/app/redux/hooks/wordMats';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { WordMat } from '@/common/types/wordMat';
import {
  DashboardViewActions,
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import SearchInput from '@/common/ui/SearchInput';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  ArrowSquareOutIcon,
  CopySimpleIcon,
  DotsThreeVerticalIcon,
  ListIcon,
  PlusIcon,
  PresentationIcon,
  SquaresFourIcon,
} from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { ToggleGroup, ToggleGroupItem } from '@shadcn/components/toggle-group';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DEFAULT_CATEGORY_COLOR_INDEX, WORD_MAT_COLORS } from './colors';
import { getAlphabetItems, getGraphemeItems } from './WordMat';
import WordMatCard from './WordMatCard';
import WordMatPresentMode from './WordMatPresentMode';
import WordMatsTableRow from './WordMatsTableRow';

/** Snapshot the default English word mat as custom categories for copy-on-write. */
function getDefaultWordMatSnapshot() {
  const alphabetItems = getAlphabetItems('en');
  const graphemeCategories = getGraphemeItems('en');

  // Strip undefined values (Firebase rejects them)
  function cleanItems(items: import('@/common/types/wordMat').WordMatItem[]) {
    return items.map((item) => {
      const clean: import('@/common/types/wordMat').WordMatItem = {
        id: item.id,
        text: item.text,
      };
      if (item.color !== undefined) clean.color = item.color;
      return clean;
    });
  }

  const customCategories: Record<
    string,
    import('@/common/types/wordMat').WordMatItem[]
  > = {
    Alphabet: cleanItems(alphabetItems),
  };
  for (const [name, items] of Object.entries(graphemeCategories)) {
    customCategories[name] = cleanItems(items);
  }

  const categoryNames = Object.keys(customCategories);
  const customCategoryColors: Record<string, string> = {};
  categoryNames.forEach((name, i) => {
    const colorIndex = DEFAULT_CATEGORY_COLOR_INDEX[name];
    customCategoryColors[name] =
      WORD_MAT_COLORS[colorIndex ?? i % WORD_MAT_COLORS.length].bgClass;
  });

  return { customCategories, customCategoryColors };
}

type ViewMode = 'gallery' | 'list';

export default function WordMatsView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const userId = useAppSelector(selectUserId);

  const { wordMats, isLoading } = useWordMats();
  const [deleteWordMat] = useDeleteWordMatMutation();
  const [createWordMat] = useCreateWordMatMutation();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(
    getFromLocalStorage('wordmats-view-mode') || 'gallery',
  );

  // Persist view mode preference
  useEffect(() => {
    saveToLocalStorage('wordmats-view-mode', viewMode);
  }, [viewMode]);

  const filteredWordMats = useMemo(() => {
    if (!searchTerm) return wordMats;
    return wordMats.filter((wm: WordMat) =>
      wm.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, wordMats]);

  function handleCreateWordMat() {
    navigate('/dashboard/wordmats/new');
  }

  async function handleConfirmDeleteWordMat(wordMat: WordMat) {
    setConfirmationDialogueCallback(async () => {
      await handleDeleteWordMat(wordMat);
    });
    dispatch(
      openConfirmationDialogue({
        title: 'Delete Word Mat',
        description: `Are you sure you want to delete "${wordMat.name}"?`,
        variant: 'destructive',
      }),
    );
  }

  async function handleDeleteWordMat(wordMat: WordMat) {
    try {
      await deleteWordMat({
        orgId: currentOrganisationId!,
        id: wordMat.id,
      }).unwrap();
      trackMixpanelEvent('Word Mat Deleted', {
        wordMatId: wordMat.id,
        wordMatName: wordMat.name,
      });
      toast.success('Word mat deleted');
    } catch (e) {
      console.error('Error deleting word mat:', e);
      toast.error('Failed to delete word mat', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  // Present mode state
  const [isPresentOpen, setIsPresentOpen] = useState(false);
  const [presentData, setPresentData] = useState<
    import('./WordMat').WordMatInitialData | undefined
  >(undefined);

  function handlePresentDefault() {
    setPresentData(undefined);
    setIsPresentOpen(true);
    trackMixpanelEvent('Word Mat Presented', {
      wordMatName: 'Spellcaster Word Mat',
      isDefault: true,
    });
  }

  function handlePresentWordMat(wm: WordMat) {
    setPresentData({
      paletteMode: wm.paletteMode,
      language: wm.language,
      visibleCategories: wm.visibleCategories,
      customCategories: wm.customCategories,
      customCategoryColors: wm.customCategoryColors,
      tileSize: wm.tileSize,
      interactionMode: wm.interactionMode,
      showColors: wm.showColors,
      alphabetColorCode: wm.alphabetColorCode,
      tileSwapEnabled: wm.tileSwapEnabled,
    });
    setIsPresentOpen(true);
    trackMixpanelEvent('Word Mat Presented', {
      wordMatId: wm.id,
      wordMatName: wm.name,
      isDefault: false,
    });
  }

  async function handleCopyDefaultWordMat() {
    if (!userId || !currentOrganisationId) {
      console.error('Missing user ID or org ID');
      toast.error('Failed to copy word mat', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      const { customCategories, customCategoryColors } =
        getDefaultWordMatSnapshot();
      const newWordMat = await createWordMat({
        createdBy: userId,
        organisationId: currentOrganisationId,
        name: 'Copy of Spellcaster Word Mat',
        paletteMode: 'custom',
        customCategories,
        customCategoryColors,
        language: 'en',
        tileSize: 100,
        showColors: true,
        alphabetColorCode: true,
        tileSwapEnabled: true,
      }).unwrap();
      trackMixpanelEvent('Word Mat Copied', {
        wordMatId: newWordMat.id,
        wordMatName: 'Copy of Spellcaster Word Mat',
        sourceWordMatName: 'Spellcaster Word Mat',
        isDefaultSource: true,
      });
      toast.success('Word mat copied');
    } catch (e) {
      console.error('Error copying word mat:', e);
      toast.error('Failed to copy word mat', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  async function handleCopyWordMat(wordMat: WordMat) {
    try {
      const { id, createdAt, updatedAt, ...rest } = wordMat;
      const newWordMat = await createWordMat({
        ...rest,
        createdBy: userId ?? rest.createdBy,
        name: `Copy of ${wordMat.name}`,
      }).unwrap();
      trackMixpanelEvent('Word Mat Copied', {
        wordMatId: newWordMat.id,
        wordMatName: newWordMat.name,
        sourceWordMatId: wordMat.id,
        sourceWordMatName: wordMat.name,
        isDefaultSource: false,
      });
      toast.success('Word mat copied');
    } catch (e) {
      console.error('Error copying word mat:', e);
      toast.error('Failed to copy word mat', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  // Skeleton for gallery view
  function GallerySkeleton() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <Skeleton className="h-32 w-full" />
            <div className="p-3">
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Skeleton for list view
  function ListSkeleton() {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(6)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-5 w-40" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-8" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // Check if default word mat should be shown based on search
  const showDefaultWordMat =
    !searchTerm ||
    'spellcaster word mat'.includes(searchTerm.toLowerCase()) ||
    searchTerm
      .toLowerCase()
      .split(' ')
      .some((term) => 'spellcaster word mat'.includes(term));

  function handleOpenDefaultWordMat() {
    navigate('/dashboard/wordmats/default');
  }

  // Default Spellcaster Word Mat card for gallery view
  function DefaultWordMatCard() {
    const [isDropdownOpen, setDropdownOpen] = useState(false);

    return (
      <div
        className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={handleOpenDefaultWordMat}
      >
        {/* Preview area with gradient background */}
        <div className="relative flex items-center justify-center h-32 bg-gradient-to-br from-violet-100 to-indigo-100">
          <SquaresFourIcon
            size={24}
            weight="duotone"
            className="absolute bottom-2 left-2 text-gray-600 opacity-60"
          />
        </div>

        {/* Card footer */}
        <div className="p-3 border-t border-gray-100">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-sm text-gray-900 truncate">
                  Spellcaster Word Mat
                </h3>
                <Badge variant="secondary" className="text-xxs px-1.5 py-0">
                  Default
                </Badge>
              </div>
              <span className="text-xs text-gray-500">
                View only, make a copy to edit
              </span>
            </div>

            {/* Actions dropdown */}
            <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DotsThreeVerticalIcon size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuItem
                  className="flex gap-2"
                  onClick={() => {
                    setDropdownOpen(false);
                    handlePresentDefault();
                  }}
                >
                  <PresentationIcon size={16} /> Present
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="flex gap-2"
                  onClick={() => {
                    setDropdownOpen(false);
                    handleCopyDefaultWordMat();
                  }}
                >
                  <CopySimpleIcon size={16} /> Make a copy
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="flex gap-2"
                  onClick={() => {
                    setDropdownOpen(false);
                    window.open('/wordmat', '_blank');
                  }}
                >
                  <ArrowSquareOutIcon size={16} /> Open in new tab
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  }

  // Default Spellcaster Word Mat row for list view
  function DefaultWordMatTableRow() {
    const [isDropdownOpen, setDropdownOpen] = useState(false);

    return (
      <TableRow className="cursor-pointer" onClick={handleOpenDefaultWordMat}>
        <TableCell>
          <div className="flex items-center gap-3 font-medium">
            Spellcaster Word Mat
            <Badge variant="secondary" className="text-xxs px-1.5 py-0">
              Default
            </Badge>
          </div>
        </TableCell>
        <TableCell className="text-neutral-500">—</TableCell>
        <TableCell className="flex justify-end">
          <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="p-1/2"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVerticalIcon size={24} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  handlePresentDefault();
                }}
              >
                <PresentationIcon size={16} /> Present
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  handleCopyDefaultWordMat();
                }}
              >
                <CopySimpleIcon size={16} /> Make a copy
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  window.open('/wordmat', '_blank');
                }}
              >
                <ArrowSquareOutIcon size={16} /> Open in new tab
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <DashboardViewContainer>
        <DashboardViewHeader>
          <DashboardViewTitle>
            Word Mats
            <Badge className="px-1 text-xxs" variant="secondary">
              {wordMats.length}
            </Badge>
          </DashboardViewTitle>
          <DashboardViewActions>
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value: ViewMode) => value && setViewMode(value)}
              className="border rounded-md"
            >
              <ToggleGroupItem
                value="gallery"
                aria-label="Gallery view"
                className="px-2"
              >
                <SquaresFourIcon size={18} />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="list"
                aria-label="List view"
                className="px-2"
              >
                <ListIcon size={18} />
              </ToggleGroupItem>
            </ToggleGroup>
            <SearchInput
              placeholder="Search word mats..."
              value={searchTerm}
              onChange={setSearchTerm}
            />
            <Button
              variant="outline"
              className="flex gap-2"
              onClick={handleCreateWordMat}
            >
              <PlusIcon size={20} />
              Create Word Mat
            </Button>
          </DashboardViewActions>
        </DashboardViewHeader>

        <DashboardViewContent>
          {isLoading ? (
            viewMode === 'gallery' ? (
              <GallerySkeleton />
            ) : (
              <ListSkeleton />
            )
          ) : showDefaultWordMat || filteredWordMats.length > 0 ? (
            viewMode === 'gallery' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                {showDefaultWordMat && <DefaultWordMatCard />}
                {filteredWordMats.map((wordMat) => (
                  <WordMatCard
                    key={wordMat.id}
                    wordMat={wordMat}
                    onPresent={() => handlePresentWordMat(wordMat)}
                    onCopy={() => handleCopyWordMat(wordMat)}
                    onConfirmDelete={() => handleConfirmDeleteWordMat(wordMat)}
                  />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {showDefaultWordMat && <DefaultWordMatTableRow />}
                  {filteredWordMats.map((wordMat) => (
                    <WordMatsTableRow
                      key={wordMat.id}
                      wordMat={wordMat}
                      onPresent={() => handlePresentWordMat(wordMat)}
                      onCopy={() => handleCopyWordMat(wordMat)}
                      onConfirmDelete={() =>
                        handleConfirmDeleteWordMat(wordMat)
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            <div className="w-full min-h-[70vh] flex flex-col items-center justify-center py-12 gap-6">
              <SquaresFourIcon
                size={80}
                className="text-neutral-400"
                weight="thin"
              />
              <div className="flex flex-col items-center gap-2">
                <h3 className="text-lg font-medium text-neutral-900">
                  No matching word mats
                </h3>
                <p className="text-sm text-neutral-500 max-w-[400px] text-center">
                  Try a different search term or create a new word mat.
                </p>
              </div>
              <Button onClick={handleCreateWordMat}>
                <PlusIcon size={20} className="mr-2" />
                Create Word Mat
              </Button>
            </div>
          )}
        </DashboardViewContent>
      </DashboardViewContainer>

      {isPresentOpen && (
        <WordMatPresentMode
          initialData={presentData}
          onClose={() => setIsPresentOpen(false)}
        />
      )}
    </>
  );
}
